import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, ArrowLeft } from 'lucide-react';

export default function TermsOfServicePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#141414]">
      {/* Header */}
      <header className="max-w-[800px] mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/')}>
          <div className="w-8 h-8 bg-[#7B2FFF] rounded-md flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" />
          </div>
          <span className="text-lg font-bold tracking-tight text-[#EDEDF0]" style={{ fontFamily: "'Unbounded', sans-serif" }}>
            HighConnect
          </span>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-[#EDEDF0] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </header>

      {/* Content */}
      <main className="max-w-[800px] mx-auto px-6 py-8 pb-20" data-testid="terms-of-service-page">
        <p className="text-xs text-[#71717A] mb-2">Last Updated: May 2026</p>
        <h1
          className="text-3xl sm:text-4xl font-bold text-[#EDEDF0] mb-8"
          style={{ fontFamily: "'Unbounded', sans-serif" }}
        >
          Terms of Service
        </h1>

        <div className="prose-custom space-y-6 text-sm text-[#A1A1AA] leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-[#EDEDF0] mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              Service Description
            </h2>
            <p>
              HighConnect is an AI automation middleware that connects GoHighLevel CRM accounts to OpenAI language models, 
              enabling automated message responses to leads and contacts. The service is operated by Rochelle Weber, 
              Gold Coast, Queensland, Australia.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#EDEDF0] mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              Service Availability
            </h2>
            <p>
              The service is provided <strong className="text-[#EDEDF0]">"as-is"</strong> with no uptime guarantee. 
              While we strive to maintain high availability, we do not warrant uninterrupted or error-free operation. 
              Scheduled maintenance and unforeseen outages may occur.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#EDEDF0] mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              User Responsibilities
            </h2>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>
                You are responsible for your own GoHighLevel account compliance, including adherence to 
                GoHighLevel's terms of service and any applicable messaging regulations (e.g., TCPA, spam laws).
              </li>
              <li>
                You are responsible for your own OpenAI API usage and costs. HighConnect uses a Bring Your Own Key 
                (BYOK) model — API charges are billed directly by OpenAI to your account.
              </li>
              <li>
                You are responsible for the content and accuracy of your bot prompt configuration. You control 
                what the AI says to your leads.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#EDEDF0] mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              Limitation of Liability
            </h2>
            <p>
              HighConnect is <strong className="text-[#EDEDF0]">not liable for AI responses sent to leads</strong>. 
              The user controls the bot prompt and is responsible for the output generated by the AI model. 
              HighConnect acts solely as a middleware layer routing messages between GoHighLevel and OpenAI.
            </p>
            <p className="mt-2">
              In no event shall HighConnect or its operator be liable for any indirect, incidental, special, 
              consequential, or punitive damages arising from the use of this service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#EDEDF0] mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              Modifications and Discontinuation
            </h2>
            <p>
              The service may be modified or discontinued with reasonable notice. We will make reasonable efforts 
              to notify active users of significant changes via email. Continued use of the service after 
              modifications constitutes acceptance of the updated terms.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#EDEDF0] mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              Governing Law
            </h2>
            <p>
              These terms are governed by and construed in accordance with the laws of{' '}
              <strong className="text-[#EDEDF0]">Queensland, Australia</strong>. Any disputes shall be subject 
              to the exclusive jurisdiction of the courts of Queensland.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#EDEDF0] mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              Contact
            </h2>
            <p>
              For questions regarding these terms, contact us at{' '}
              <a href="mailto:rochelle@fastreply247.com" className="text-[#7B2FFF] hover:text-[#9353FF] underline">
                rochelle@fastreply247.com
              </a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#27272A] py-6">
        <div className="max-w-[800px] mx-auto px-6 flex items-center justify-between">
          <span className="text-xs text-[#71717A]">HighConnect</span>
          <div className="flex items-center gap-4">
            <a href="/privacy-policy" className="text-xs text-[#71717A] hover:text-[#EDEDF0] transition-colors">Privacy Policy</a>
            <span className="text-xs text-[#7B2FFF]">Terms of Service</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
